import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  cancelStays,
  convertPreview,
  getStaysBilling,
  getStaysInvoices,
  previewConvert,
  previewSlots,
  resumeStays,
  setSlots,
  setupConvert,
  StaysBilling,
  StaysConvertQuote,
  StaysInvoice,
  StaysSlotQuote,
} from '../../../../api/stays-billing-client';
import { StaysApiError } from '../../../../api/stays-client';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { StateSwitch } from '../../../../motion';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { Field, FieldError, FieldHint, Input, Label } from '../../../form-kit';
import { ListSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { InfoBanner } from '../../../onboarding/onboarding.styled';
import { StripeCardModal, StripeCardConfirmation, StripeIntent } from '../../../payments/stripe-card-modal';
import { toast } from '../../../../utils/toast';
import { formatDate } from '../../../../function-utils/locale';
import { StaysSectionTabs } from '../stays-section';
import { ProductsCard } from '../../../billing/products-card';
import { Quiet, StepTitle, Why } from '../setup/setup-wizard.styled';
import { CardList, CardMeta, CardTitle, Inline, KindCard, KindGroup, KindHint, KindTitle, Page, PageHead, PropertyCard } from '../properties/properties.styled';

/**
 * STAYS → BILLING — /dashboard/stays/billing (Independent Stays IS4.8).
 *
 * One page, three accounts (BE src/stays/billing/service.js `account`):
 *   - preview: a Stays-only account with no plan. With billing on it offers
 *     "Choose a plan" (IS4.10): a plan, how many places, the server's quote,
 *     then a card (none under FREE_SIGNUP).
 *   - host: a Stays-only account on a plan. The plan, its places, and for the
 *     per-check-in plan this period's completed check-ins. The plan and its
 *     places change on /dashboard/billing (the one pool).
 *   - addon: a Lobbero Desk customer with Stays. Listing slots, priced by the
 *     server before anything changes (QUOTE_CHANGED if the price moved).
 *   - bundle: Desk + Stays at one price (IS6). Every listing is included.
 * Above them, on every account while billing is on: "Your products" (IS6).
 *
 * Every amount is the server's integer cents. Nothing here multiplies a price.
 */

const money = (cents: number, currency = 'usd'): string =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
// Billing dates through the app's one locale module (the building-time guard).
const day = (iso: string | null): string => (iso ? formatDate(iso, { day: 'numeric', month: 'long', year: 'numeric' }) : '');
const problem = (e: unknown, fallback: string) => refusalText(e instanceof StaysApiError ? e : null, fallback);

const STATUS_WORDS: Record<string, { tone: 'success' | 'warning' | 'neutral' | 'alert'; label: string }> = {
  active: { tone: 'success', label: 'Active' },
  trialing: { tone: 'success', label: 'Free trial' },
  past_due: { tone: 'alert', label: 'Payment due' },
  pending_card: { tone: 'warning', label: 'Waiting for a card' },
  canceled: { tone: 'neutral', label: 'Ended' },
  none: { tone: 'neutral', label: 'Not started' },
};
const statusPill = (s: string) => {
  const w = STATUS_WORDS[s] || { tone: 'neutral' as const, label: s };

  return <StatusPill tone={w.tone}>{w.label}</StatusPill>;
};

export const StaysBillingPage: React.FC = () => {
  const { permissions } = useAuth();
  const canManage = can(permissions, PERMISSIONS.BILLING_MANAGE);
  const [data, setData] = useState<StaysBilling | null>(null);
  const [view, setView] = useState<'loading' | 'error' | 'off' | 'ready'>('loading');
  const [invoices, setInvoices] = useState<StaysInvoice[] | null>(null);

  const load = useCallback((quiet = false) => {
    if (!quiet) setView('loading');
    getStaysBilling()
      .then(d => {
        setData(d);
        setView('ready');
        if (d.kind) {
          getStaysInvoices()
            .then(r => setInvoices(r.invoices))
            .catch(() => setInvoices(null));
        }
      })
      // A typed address from an organisation without Stays: the API's
      // STAYS_NOT_ENABLED is not a load failure, and Retry would never help.
      .catch(e => !quiet && setView(e instanceof StaysApiError && e.code === 'STAYS_NOT_ENABLED' ? 'off' : 'error'));
  }, []);
  useEffect(() => load(), [load]);

  return (
    <Page data-stays-billing-page="">
      <StaysSectionTabs />
      <PageHead>
        <div>
          <StepTitle as="h2">Billing</StepTitle>
          <Why>What Lobbero Stays costs your account, and what you have used.</Why>
        </div>
      </PageHead>
      <StateSwitch state={view}>
        {view === 'loading' ? (
          <ListSkeleton rows={3} label="Stays billing" />
        ) : view === 'off' ? (
          <Quiet data-stays-not-enabled="">Lobbero Stays is not switched on for your organisation. Nothing is charged.</Quiet>
        ) : view === 'error' || !data ? (
          <LoadError message="Could not load Stays billing." onRetry={() => load()} />
        ) : !data.enabled ? (
          <Quiet>
            {data.account === 'preview'
              ? 'Your account is on the free preview. Plans open soon; nothing is charged.'
              : 'Billing for Lobbero Stays is not switched on yet. Nothing is charged.'}
          </Quiet>
        ) : (
          <>
            {data.account === 'preview' ? null : <ProductsCard onChanged={() => load(true)} />}
            {data.account === 'preview' ? (
              <ChoosePlan data={data} canManage={canManage} onDone={() => load(true)} />
            ) : data.account === 'host' ? (
              <HostPlan data={data} invoices={invoices} />
            ) : data.account === 'addon' ? (
              <ListingSlots data={data} invoices={invoices} canManage={canManage} onChanged={d => setData(d)} />
            ) : data.account === 'bundle' ? (
              <Quiet data-stays-bundle="">
                Every listing is included in Lobbero Desk + Stays. <Link to="/dashboard/billing">See your plan on Billing</Link>
              </Quiet>
            ) : (
              <Quiet>Lobbero Stays is not switched on for your organisation.</Quiet>
            )}
          </>
        )}
      </StateSwitch>
    </Page>
  );
};


// ── preview → paid (IS4.10) ─────────────────────────────────────────────────
const ChoosePlan: React.FC<{ data: StaysBilling; canManage: boolean; onDone: () => void }> = ({ data, canManage, onDone }) => {
  const cap = data.preview ? data.preview.cap : 0;
  const used = data.preview ? data.preview.used : 0;
  const [planCode, setPlanCode] = useState('');
  const [count, setCount] = useState(String(Math.max(1, used)));
  const [quote, setQuote] = useState<StaysConvertQuote | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [card, setCard] = useState(false);
  const properties = Number(count);

  const plans = [
    data.prices.listing_cents !== null && {
      code: 'stays_listing',
      title: `${money(data.prices.listing_cents)} per place to stay / month`,
      hint: 'One price per place to stay, however many guests check in.',
    },
    data.prices.checkin_cents !== null && {
      code: 'stays_checkin',
      title: `${money(data.prices.checkin_cents)} per completed check-in`,
      hint: 'No monthly fee per place. You pay when a guest completes their online check-in.',
    },
  ].filter(Boolean) as { code: string; title: string; hint: string }[];

  const price = async () => {
    setError('');
    if (!planCode) return setError('Choose a plan.');
    if (!Number.isInteger(properties) || properties < 1) return setError('Choose how many places to stay.');
    setBusy(true);
    try {
      setQuote(await previewConvert(planCode, properties));
    } catch (e) {
      setError(problem(e, 'Could not price that plan. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const finish = async (setupIntentId: string | null) => {
    await convertPreview(planCode, properties, setupIntentId);
    toast.success('Your plan is active.');
    setCard(false);
    onDone();
  };

  const start = async () => {
    if (!quote) return;
    if (quote.card_required) return setCard(true);
    setBusy(true);
    try {
      await finish(null);
    } catch (e) {
      setError(problem(e, 'Could not start your plan. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const prepareIntent = async (): Promise<StripeIntent> => {
    const s = await setupConvert();

    return { mode: 'setup', clientSecret: s.client_secret };
  };
  const onConfirmed = async (r: StripeCardConfirmation) => {
    if (r.confirmed !== 'confirmCardSetup' || !r.intentId) throw new Error('The card was not confirmed. Nothing was charged.');
    await finish(r.intentId);
  };

  return (
    <div data-stays-choose-plan="">
      <InfoBanner tone="brand" role="status">
        You&rsquo;re on the free preview ({cap} {cap === 1 ? 'property' : 'properties'}, {used} used). Choose a plan to
        add more.
      </InfoBanner>
      {!canManage ? (
        <Quiet>Only the account owner can choose a plan.</Quiet>
      ) : (
        <>
          <KindGroup role="radiogroup" aria-label="Plan">
            {plans.map(p => (
              <KindCard
                key={p.code}
                type="button"
                role="radio"
                aria-checked={planCode === p.code}
                $checked={planCode === p.code}
                data-plan-code={p.code}
                onClick={() => {
                  setPlanCode(p.code);
                  setQuote(null);
                }}
              >
                <KindTitle>{p.title}</KindTitle>
                <KindHint>{p.hint}</KindHint>
              </KindCard>
            ))}
          </KindGroup>
          <Field>
            <Label htmlFor="sb-properties">How many places to stay?</Label>
            <Input
              id="sb-properties"
              type="number"
              inputMode="numeric"
              min={Math.max(1, used)}
              value={count}
              onChange={e => {
                setCount(e.target.value);
                setQuote(null);
              }}
            />
            <FieldHint>You have {used} now. Each apartment or whole place counts as one.</FieldHint>
          </Field>
          <FieldError message={error} />
          {quote ? (
            <PropertyCard as="div" data-stays-convert-quote="">
              <div>
                <CardTitle>{quote.plan_name}</CardTitle>
                <CardMeta>
                  {quote.model === 'per_checkin'
                    ? `${money(quote.fee_cents, quote.currency)} for each completed check-in`
                    : `${quote.properties} × ${money(quote.unit_cents, quote.currency)} = ${money(quote.monthly_cents, quote.currency)} / month`}
                </CardMeta>
                <CardMeta>
                  {quote.free_signup
                    ? 'Complimentary for now. No card is needed.'
                    : `Due today: ${money(quote.due_today_cents, quote.currency)}. No free trial when a preview chooses a plan.`}
                </CardMeta>
              </div>
              <Inline>
                <AppButton onClick={start} loading={busy}>
                  {quote.card_required ? 'Add a card and start' : 'Start my plan'}
                </AppButton>
              </Inline>
            </PropertyCard>
          ) : (
            <Inline>
              <AppButton onClick={price} loading={busy}>
                See the price
              </AppButton>
            </Inline>
          )}
          {card && quote ? (
            <StripeCardModal
              open={card}
              title="Your card"
              collectCard
              prepareIntent={prepareIntent}
              onConfirmed={onConfirmed}
              summaryRows={[
                { label: 'Plan', value: quote.plan_name },
                { label: 'Due today', value: money(quote.due_today_cents, quote.currency), total: true },
              ]}
              submitLabel="Start my plan"
              busyLabel="Starting…"
              cancelLabel="Back"
              onCancel={() => setCard(false)}
              onDismiss={() => setCard(false)}
              fallbackErrorMessage="Could not start your plan. Please try again."
            />
          ) : null}
        </>
      )}
    </div>
  );
};

// ── a Stays-only account on a plan ─────────────────────────────────────────
const HostPlan: React.FC<{ data: StaysBilling; invoices: StaysInvoice[] | null }> = ({ data, invoices }) => {
  const p = data.plan;
  if (!p) return <Quiet>No plan yet.</Quiet>;
  const perCheckin = p.model === 'per_checkin';

  return (
    <CardList aria-label="Your Stays plan">
      <PropertyCard data-stays-host-plan="">
        <div>
          <CardTitle>
            {p.name} {statusPill(p.status)}
          </CardTitle>
          <CardMeta>
            {p.properties} {p.properties === 1 ? 'place to stay' : 'places to stay'}
            {p.comped ? ' · Complimentary' : ''}
            {p.trial_end && p.status === 'trialing' ? ` · Free trial until ${day(p.trial_end)}` : ''}
            {p.cancel_at_period_end ? ' · Ends at the end of this period' : ''}
          </CardMeta>
          {perCheckin ? (
            <CardMeta data-stays-usage="">
              This period: {data.usage.checkins} completed{' '}
              {data.usage.checkins === 1 ? 'check-in' : 'check-ins'}
              {data.usage.free_trial
                ? ' (free during your trial)'
                : ` · about ${money(data.usage.estimated_cents, data.currency)}`}
            </CardMeta>
          ) : null}
        </div>
        <Inline>
          <Link to="/dashboard/billing">Add places, change your card or see invoices on Billing</Link>
        </Inline>
      </PropertyCard>
      {perCheckin ? <Invoices invoices={invoices} /> : null}
    </CardList>
  );
};

// ── a Desk customer's listing slots ────────────────────────────────────────
const ListingSlots: React.FC<{
  data: StaysBilling;
  invoices: StaysInvoice[] | null;
  canManage: boolean;
  onChanged: (d: StaysBilling) => void;
}> = ({ data, invoices, canManage, onChanged }) => {
  const [target, setTarget] = useState(String(data.listing_quantity));
  const [quote, setQuote] = useState<StaysSlotQuote | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [stopping, setStopping] = useState(false);
  const n = Number(target);
  const comped = data.comped;

  const price = async () => {
    setError('');
    if (!Number.isInteger(n) || n < 0) return setError('Choose a number of listing slots.');
    setBusy(true);
    try {
      setQuote(await previewSlots(n));
    } catch (e) {
      setError(problem(e, 'Could not price that change. Please try again.'));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    if (!quote) return;
    setBusy(true);
    try {
      const d = await setSlots(n, quote.next_invoice_cents);
      onChanged(d);
      setQuote(null);
      toast.success('Your listing slots were changed.');
    } catch (e) {
      // The price moved: show the new quote and ask again.
      const extra = e instanceof StaysApiError ? (e.extra as { quote?: StaysSlotQuote }) : null;
      if (extra && extra.quote) setQuote(extra.quote);
      setError(problem(e, 'Could not change your listing slots. Please try again.'));
    } finally {
      setBusy(false);
    }
  };
  const stopOrResume = async (stop: boolean) => {
    setBusy(true);
    try {
      onChanged(await (stop ? cancelStays() : resumeStays()));
      setStopping(false);
    } catch (e) {
      setError(problem(e, 'Could not change that. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CardList aria-label="Listing slots">
      <PropertyCard data-stays-slots="">
        <div>
          <CardTitle>
            Listing slots {statusPill(data.status)}
          </CardTitle>
          <CardMeta>
            {data.listings_used} of {data.listing_quantity} in use · {data.remaining} free
            {data.prices.addon_cents !== null ? ` · ${money(data.prices.addon_cents, data.currency)} per slot / month` : ''}
          </CardMeta>
          <CardMeta>Every listing that is not retired holds a slot.</CardMeta>
          {data.cancel_at_period_end && data.current_period_end ? (
            <CardMeta>Stays billing ends on {day(data.current_period_end)}.</CardMeta>
          ) : null}
        </div>
        {comped ? (
          <Quiet>This is a complimentary account. Billing changes are made by Lobbero.</Quiet>
        ) : canManage ? (
          <>
            <Field>
              <Label htmlFor="sb-slots">Listing slots</Label>
              <Input
                id="sb-slots"
                type="number"
                inputMode="numeric"
                min={data.listings_used}
                value={target}
                onChange={e => {
                  setTarget(e.target.value);
                  setQuote(null);
                }}
              />
            </Field>
            <FieldError message={error} />
            {quote ? (
              <div data-stays-slot-quote="">
                <CardMeta>
                  {quote.target} {quote.target === 1 ? 'slot' : 'slots'}: {money(quote.recurring_cents, quote.currency)} / month.
                  {quote.delta !== 0 ? ` Next invoice: ${money(quote.next_invoice_cents, quote.currency)}.` : ''}
                </CardMeta>
                <Inline>
                  <AppButton onClick={confirm} loading={busy} disabled={quote.delta === 0}>
                    Confirm
                  </AppButton>
                  <AppButton variant="secondary" onClick={() => setQuote(null)} disabled={busy}>
                    Back
                  </AppButton>
                </Inline>
              </div>
            ) : (
              <Inline>
                <AppButton onClick={price} loading={busy}>
                  See the price
                </AppButton>
                {data.kind ? (
                  data.cancel_at_period_end ? (
                    <AppButton variant="secondary" onClick={() => stopOrResume(false)} disabled={busy}>
                      Keep paying for Stays
                    </AppButton>
                  ) : (
                    <AppButton variant="ghost" onClick={() => setStopping(true)} disabled={busy}>
                      Stop paying for Stays
                    </AppButton>
                  )
                ) : null}
              </Inline>
            )}
          </>
        ) : null}
      </PropertyCard>
      <Invoices invoices={invoices} />
      <ConfirmDialog
        open={stopping}
        title="Stop paying for Stays?"
        message="Your listing slots stay until the end of this period. Retire every listing first."
        primaryLabel="Stop at period end"
        primaryVariant="destructive"
        onPrimary={() => stopOrResume(true)}
        dismissLabel="Keep paying"
        onDismiss={() => setStopping(false)}
      />
    </CardList>
  );
};

const Invoices: React.FC<{ invoices: StaysInvoice[] | null }> = ({ invoices }) =>
  invoices && invoices.length ? (
    <PropertyCard data-stays-invoices="">
      <div>
        <CardTitle>Stays invoices</CardTitle>
        {invoices.map(i => (
          <CardMeta key={i.id}>
            {day(i.period_start)} · {money(i.total_cents, i.currency)} · {i.status}
            {i.hosted_invoice_url ? (
              <>
                {' · '}
                <a href={i.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                  View
                </a>
              </>
            ) : null}
          </CardMeta>
        ))}
      </div>
    </PropertyCard>
  ) : null;
